import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Palmtree } from 'lucide-react';

import { getCompanies } from '../../../api/General/Company';
import {
  getCompanyLeaveCountries,
  getCompanyLeaveTypeRules,
  saveCompanyLeaveType,
  saveCompanyLeaveTypeRules,
} from '../../../api/Attendance/leaves';
import { CompanyView } from '../../../models/general';
import {
  CompanyLeaveRuleView, LeaveConfigCountryView, QuotaType, QuotaUnit,
} from '../../../models/attendance';

import { PageLayout } from '../../../components/ui/PageLayout';
import Alert from '../../../components/ui/Alert';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Switch from '../../../components/ui/Switch';
import { Skeleton } from '../../../components/ui/Skeleton';
import StickyActionBar from '../../../components/ui/StickyActionBar';
import { useToast, ToastRenderer } from '../../../hooks/useToast';

/**
 * Operator-facing, CONFIGURATION-ONLY leave surface.
 *
 * Owner carve-out 2026-07-29, re-based onto the Policies API surface 2026-09-04
 * (docs/leave-policies/SPEC-operator-cross-tenant-policies.md). Scope is exactly the four
 * `companyId`-addressed config routes:
 *   - GET  Leave/Policies/Company/{companyId}/Countries        (the country axis)
 *   - GET  Leave/Policies/Company/{companyId}/LeaveTypeRules   (the grid — a pure read)
 *   - PUT  Leave/Policies/Company/{companyId}/LeaveTypeRules   (proportional batch upsert)
 *   - POST Leave/Policies/Company/{companyId}/LeaveTypes       (create a leave type)
 *
 * NOTHING employee-scoped is fetched or rendered here: no employee list, no leave balances, no leave
 * requests, no quota-per-user projection. That is true by construction now rather than by convention —
 * none of the four routes accepts a user id or projects a `User`, and the tenant comes from the route
 * rather than from a field in the body that a caller could disagree with.
 *
 * The country axis is no longer derived only from employees. Owner decision 2026-09-04: an Operator
 * may configure a country the tenant has no employees in yet (the picker's "every country" mode), which
 * is what makes seeding statutory leave into a fresh tenant possible at all.
 *
 * HR keeps its own richer surface at /leave-policies (Policies model). This page is not offered to HR:
 * the company picker is fed by `Company/GetCompanies`, which is Operator-only.
 */

/* ─────────────────────────── helpers ─────────────────────────── */

type Row = CompanyLeaveRuleView;

const rowKey = (row: Row) => `${row.leaveTypeId}-${row.countryId}`;

/** Stable serialization used for dirty-checking (row identity, not object identity). */
const snapshot = (rows: Row[]) =>
  JSON.stringify(
    [...rows]
      .sort((a, b) => rowKey(a).localeCompare(rowKey(b)))
      .map(r => [
        rowKey(r), r.enabled, r.eligibility, r.quotaType, r.quotaUnit, r.isCummulative,
        r.annuallyResets, r.includeNonWorkingrDays, r.leaveOverlapsOtherLeaves,
        r.attachmentRequired, r.warningThreshold ?? null,
      ]),
  );

interface NumberFieldProps {
  label: string;
  description?: string;
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  placeholder?: string;
  testId: string;
}

const NumberField = ({ label, description, value, onChange, placeholder, testId }: NumberFieldProps) => (
  <Input
    type="number"
    min={0}
    label={label}
    description={description}
    placeholder={placeholder}
    data-testid={testId}
    value={value ?? ''}
    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (raw === '') return onChange(null);
      const parsed = Number(raw);
      onChange(Number.isFinite(parsed) ? Math.max(0, parsed) : null);
    }}
  />
);

/* ───────────────────────── country row ───────────────────────── */

interface CountryRowProps {
  row: Row;
  onPatch: (patch: Partial<Row>) => void;
  t: (key: string) => string;
}

const CountryRow = ({ row, onPatch, t }: CountryRowProps) => {
  const switchId = `leave-cfg-${rowKey(row)}`;
  const countryName = row.countryName?.trim() || t('Unknown country');

  const quotaTypeOptions = useMemo(
    () => Object.values(QuotaType).map(v => ({ value: v, label: t(v) })),
    [t],
  );
  const quotaUnitOptions = useMemo(
    () => Object.values(QuotaUnit).map(v => ({ value: v, label: t(v) })),
    [t],
  );

  return (
    <div
      className="px-5 py-4"
      data-testid="leave-config-row"
      data-leave-type-id={row.leaveTypeId}
      data-country-id={row.countryId}
      data-persisted={row.isPersisted ? 'true' : 'false'}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <Switch
          id={switchId}
          data-testid="leave-config-enabled"
          checked={row.enabled}
          onCheckedChange={(checked) => onPatch({ enabled: checked })}
          aria-label={countryName}
        />
        <label
          htmlFor={switchId}
          className="min-w-0 cursor-pointer break-words text-sm font-medium text-foreground"
        >
          {countryName}
        </label>
        {!row.enabled && (
          <span className="text-xs text-muted-foreground">{t('Not offered in this country')}</span>
        )}
      </div>

      {row.enabled && (
        <div className="mt-4 flex flex-col gap-5">
          {/*
            Two columns, paired by kind: the two numerics (both carrying helper text) share the first
            row, the two Selects (neither carrying helper text) share the second. A three-column grid
            stranded Warning Threshold alone on row 2 and put a helper-text field next to a
            helper-less one, so no two controls in a row sat on the same baseline.
          */}
          <div className="grid max-w-3xl gap-x-4 gap-y-5 sm:grid-cols-2">
            <NumberField
              label={t('Eligibility')}
              description={t('Service required before this leave can be taken')}
              testId="leave-config-eligibility"
              value={row.eligibility}
              onChange={(v) => onPatch({ eligibility: v ?? 0 })}
            />
            <NumberField
              label={t('Warning Threshold')}
              description={t('Warn once the remaining balance drops to this. Blank means never.')}
              placeholder={t('None')}
              testId="leave-config-warning-threshold"
              value={row.warningThreshold ?? null}
              onChange={(v) => onPatch({ warningThreshold: v })}
            />
            <Select
              label={t('Quota Type')}
              data-testid="leave-config-quota-type"
              options={quotaTypeOptions}
              value={row.quotaType}
              onChange={(v: QuotaType) => onPatch({ quotaType: v })}
            />
            <Select
              label={t('Quota Unit')}
              data-testid="leave-config-quota-unit"
              options={quotaUnitOptions}
              value={row.quotaUnit}
              onChange={(v: QuotaUnit) => onPatch({ quotaUnit: v })}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <Checkbox
              label={t('Carries unused balance forward')}
              data-testid="leave-config-is-cummulative"
              checked={row.isCummulative}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onPatch({ isCummulative: e.target.checked })}
            />
            <Checkbox
              label={t('Resets at the start of each year')}
              data-testid="leave-config-annually-resets"
              checked={row.annuallyResets}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onPatch({ annuallyResets: e.target.checked })}
            />
            <Checkbox
              label={t('Counts non-working days')}
              data-testid="leave-config-include-non-working-days"
              checked={row.includeNonWorkingrDays}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onPatch({ includeNonWorkingrDays: e.target.checked })}
            />
            <Checkbox
              label={t('Can overlap other leave')}
              data-testid="leave-config-leave-overlaps"
              checked={row.leaveOverlapsOtherLeaves}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onPatch({ leaveOverlapsOtherLeaves: e.target.checked })}
            />
            <Checkbox
              label={t('Attachment required')}
              data-testid="leave-config-attachment-required"
              checked={row.attachmentRequired}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onPatch({ attachmentRequired: e.target.checked })}
            />
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────── page ─────────────────────────── */

export default function OperatorLeaveConfiguration() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast, showToast, dismissToast } = useToast();

  const [companies, setCompanies] = useState<CompanyView[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [companiesError, setCompaniesError] = useState(false);

  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);

  /* country axis ---------------------------------------------------------- */
  const [countries, setCountries] = useState<LeaveConfigCountryView[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  /**
   * `null` means "whatever axis the server considers the default" (the union of the company's
   * employees' countries, the company's own country, and any country that already has rules).
   * An explicit array is only ever set by the operator choosing countries.
   */
  const [selectedCountryIds, setSelectedCountryIds] = useState<number[] | null>(null);
  const [includeAllCountries, setIncludeAllCountries] = useState(false);

  const [rows, setRows] = useState<Row[]>([]);
  const [baseline, setBaseline] = useState('');
  const [leaveTypesCount, setLeaveTypesCount] = useState(0);
  const [countriesCount, setCountriesCount] = useState(0);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [configError, setConfigError] = useState(false);
  const [saving, setSaving] = useState(false);
  /**
   * A failed save must not be a toast that vanishes. The sticky bar keeps reading "Unsaved changes"
   * (correctly — nothing was written), so the operator needs a message that stays on screen next to
   * the edits it refused.
   */
  const [saveError, setSaveError] = useState<string | null>(null);

  const [newTypeName, setNewTypeName] = useState('');
  const [creating, setCreating] = useState(false);

  /* companies ------------------------------------------------------------ */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getCompanies();
        if (cancelled) return;
        if (res.errors?.length) {
          setCompaniesError(true);
        } else {
          setCompanies(res.payload ?? []);
        }
      } catch {
        if (!cancelled) setCompaniesError(true);
      } finally {
        if (!cancelled) setLoadingCompanies(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /* deep link (?companyId=) --------------------------------------------- */
  useEffect(() => {
    const raw = searchParams.get('companyId');
    if (!raw || companies.length === 0) return;
    const id = Number(raw);
    if (Number.isFinite(id) && companies.some(c => c.id === id)) {
      setSelectedCompanyId(prev => (prev === id ? prev : id));
    }
  }, [searchParams, companies]);

  /* countries ------------------------------------------------------------ */
  useEffect(() => {
    if (!selectedCompanyId) {
      setCountries([]);
      return;
    }
    let cancelled = false;
    setLoadingCountries(true);
    (async () => {
      try {
        const res = await getCompanyLeaveCountries(selectedCompanyId, includeAllCountries);
        if (cancelled) return;
        setCountries(res.errors?.length ? [] : (res.payload ?? []));
      } catch {
        if (!cancelled) setCountries([]);
      } finally {
        if (!cancelled) setLoadingCountries(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedCompanyId, includeAllCountries]);

  /* config --------------------------------------------------------------- */
  const fetchConfig = useCallback(async (companyId: number, countryIds: number[] | null) => {
    setLoadingConfig(true);
    setConfigError(false);
    setSaveError(null);
    try {
      // The route carries the tenant; nothing employee-scoped is requested or returned, and the
      // read is pure — it never materialises the rows it reports as unconfigured.
      const res = await getCompanyLeaveTypeRules(companyId, countryIds ?? undefined);
      if (res.errors?.length) {
        setConfigError(true);
        setRows([]);
        setBaseline('');
        return;
      }
      const list = res.payload?.rows ?? [];
      setRows(list);
      setBaseline(snapshot(list));
      setLeaveTypesCount(res.payload?.leaveTypesCount ?? 0);
      setCountriesCount(res.payload?.countriesCount ?? 0);
    } catch {
      setConfigError(true);
      setRows([]);
      setBaseline('');
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCompanyId) fetchConfig(selectedCompanyId, selectedCountryIds);
  }, [selectedCompanyId, selectedCountryIds, fetchConfig]);

  const hasUnsavedChanges = baseline !== '' && snapshot(rows) !== baseline;

  const handleCompanyChange = (value: number | string) => {
    const id = Number(value);
    if (!Number.isFinite(id)) return;
    setSelectedCompanyId(id);
    setSelectedCountryIds(null);
    setIncludeAllCountries(false);
    setSearchParams({ companyId: String(id) });
  };

  const patchRow = useCallback((key: string, patch: Partial<Row>) => {
    setRows(prev => prev.map(r => (rowKey(r) === key ? { ...r, ...patch } : r)));
  }, []);

  const save = async () => {
    if (!selectedCompanyId || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      // No `companyId` on the rows: the route is the only authority for the tenant, and
      // (leaveTypeId, countryId) is the natural key the server upserts on.
      const res = await saveCompanyLeaveTypeRules(selectedCompanyId, {
        rows: rows.map(r => ({
          leaveTypeId: r.leaveTypeId,
          countryId: r.countryId,
          enabled: r.enabled,
          eligibility: r.eligibility,
          quotaType: r.quotaType,
          quotaUnit: r.quotaUnit,
          isCummulative: r.isCummulative,
          annuallyResets: r.annuallyResets,
          includeNonWorkingrDays: r.includeNonWorkingrDays,
          leaveOverlapsOtherLeaves: r.leaveOverlapsOtherLeaves,
          attachmentRequired: r.attachmentRequired,
          warningThreshold: r.warningThreshold ?? null,
        })),
      });
      if (res.errors?.length) {
        const message = res.errors[0]?.description || t('Could not save leave configuration');
        setSaveError(message);
        showToast(message, 'error');
        return;
      }
      const written = (res.payload?.rulesCreated ?? 0) + (res.payload?.rulesUpdated ?? 0);
      showToast(
        written > 0
          ? t('Leave configuration saved. {{written}} rules written.', { written })
          : t('Leave configuration saved'),
        'success',
      );
      await fetchConfig(selectedCompanyId, selectedCountryIds);
    } catch {
      setSaveError(t('Could not save leave configuration'));
      showToast(t('Could not save leave configuration'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const addLeaveType = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newTypeName.trim();
    if (!selectedCompanyId || !name || creating) return;
    setCreating(true);
    try {
      // No `id` means create. The tenant comes from the route.
      const res = await saveCompanyLeaveType(selectedCompanyId, { name, isActive: true });
      if (res.errors?.length) {
        showToast(res.errors[0]?.description || t('Could not create the leave type'), 'error');
        return;
      }
      setNewTypeName('');
      showToast(t('Leave type created'), 'success');
      await fetchConfig(selectedCompanyId, selectedCountryIds);
    } catch {
      showToast(t('Could not create the leave type'), 'error');
    } finally {
      setCreating(false);
    }
  };

  /* grouping ------------------------------------------------------------- */
  const groups = useMemo(() => {
    const byType = new Map<number, { name: string; rows: Row[] }>();
    for (const row of rows) {
      const existing = byType.get(row.leaveTypeId);
      if (existing) {
        existing.rows.push(row);
      } else {
        byType.set(row.leaveTypeId, {
          name: row.leaveTypeName?.trim() || t('Untitled leave type'),
          rows: [row],
        });
      }
    }
    return [...byType.entries()]
      .map(([id, g]) => ({ id, ...g }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, t]);

  const companyOptions = useMemo(
    () => companies.map(c => ({ value: c.id, label: c.name })),
    [companies],
  );

  /**
   * Countries the tenant actually staffs come first, then the ones already carrying rules, then the
   * rest. The description under each option says which is which, so "this country has nobody in it
   * yet" is information rather than a hidden constraint.
   */
  const countryOptions = useMemo(() => {
    // Tier 0: the company's own country. Tier 1: countries it actually staffs. Tier 2: countries that
    // already carry rules. Tier 3: everything else (only present in "show every country" mode).
    const tier = (c: LeaveConfigCountryView) => {
      if (c.isCompanyCountry) return 0;
      if (c.employeeCount > 0) return 1;
      return c.hasRules ? 2 : 3;
    };
    return [...countries]
      .sort((a, b) => tier(a) - tier(b) || b.employeeCount - a.employeeCount || a.name.localeCompare(b.name))
      .map(c => ({
        value: c.id,
        label: c.name,
        description: [
          c.employeeCount > 0
            ? t('{{employees}} working here', { employees: c.employeeCount })
            : t('Nobody here yet'),
          c.isCompanyCountry ? t('Company country') : null,
          c.hasRules ? t('Already configured') : null,
        ].filter(Boolean).join(' · '),
      }));
  }, [countries, t]);

  /** What the picker shows when the operator has not chosen: the axis the grid actually came back with. */
  const effectiveCountryIds = useMemo(
    () => selectedCountryIds ?? [...new Set(rows.map(r => r.countryId))],
    [selectedCountryIds, rows],
  );

  /** Clearing the picker means "back to the company default", not "no countries at all". */
  const handleCountryChange = (value: number[]) => {
    const ids = Array.isArray(value) ? value.map(Number).filter(Number.isFinite) : [];
    setSelectedCountryIds(ids.length > 0 ? ids : null);
  };

  /* render --------------------------------------------------------------- */
  const renderBody = () => {
    if (!selectedCompanyId) {
      return (
        <div className="rounded-lg border border-border bg-card px-6 py-12 text-center">
          <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Palmtree size={20} aria-hidden="true" />
          </div>
          <h2 className="mt-4 text-base font-semibold text-foreground">
            {t('Pick a company to configure')}
          </h2>
          <p className="mx-auto mt-2 max-w-[52ch] text-sm text-muted-foreground">
            {t('Leave types and their per-country rules are set per company. This page shows configuration only, never employee balances or requests.')}
          </p>
        </div>
      );
    }

    if (loadingConfig) {
      return (
        <div className="flex flex-col gap-4" aria-busy="true">
          {[0, 1, 2].map(i => (
            <div key={i} className="rounded-lg border border-border bg-card">
              <div className="border-b border-border px-5 py-3.5">
                <Skeleton className="h-4 w-40" />
              </div>
              <div className="flex flex-col gap-3 px-5 py-4">
                <Skeleton className="h-6 w-56" />
                <Skeleton className="h-9 w-full" />
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (configError) {
      return (
        <Alert variant="error">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm">{t('Could not load the leave configuration for this company.')}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchConfig(selectedCompanyId, selectedCountryIds)}
            >
              {t('Try again')}
            </Button>
          </div>
        </Alert>
      );
    }

    return (
      <div className="flex flex-col gap-5" data-testid="leave-config-grid">
        {leaveTypesCount > 0 && groups.length === 0 && (
          <div
            className="rounded-lg border border-border bg-card px-6 py-10 text-center"
            data-testid="leave-config-empty-no-countries"
          >
            <h2 className="text-base font-semibold text-foreground">{t('No country selected yet')}</h2>
            <p className="mx-auto mt-2 max-w-[56ch] text-sm text-muted-foreground">
              {countriesCount > 0
                ? t('Choose one or more countries above to edit their rules.')
                : t('This company has no employees with a country set. Turn on "Show every country" above, then pick the country you want to configure. Rules can be set before anyone is hired there.')}
            </p>
          </div>
        )}

        {leaveTypesCount === 0 && (
          <div
            className="rounded-lg border border-border bg-card px-6 py-10 text-center"
            data-testid="leave-config-empty-no-types"
          >
            <h2 className="text-base font-semibold text-foreground">{t('No leave types yet')}</h2>
            <p className="mx-auto mt-2 max-w-[52ch] text-sm text-muted-foreground">
              {t('Create the first leave type below. Its per-country rules will appear here once it exists.')}
            </p>
          </div>
        )}

        {groups.map(group => {
          const enabledCount = group.rows.filter(r => r.enabled).length;
          return (
            <section
              key={group.id}
              className="rounded-lg border border-border bg-card"
              data-testid="leave-config-type-group"
              data-leave-type-id={group.id}
            >
              <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-border px-5 py-3.5">
                <h2 className="min-w-0 break-words text-sm font-semibold text-foreground">{group.name}</h2>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {t('{{enabled}} of {{total}} countries enabled', {
                    enabled: enabledCount,
                    total: group.rows.length,
                  })}
                </span>
              </header>
              <div className="divide-y divide-border">
                {group.rows.map(row => (
                  <CountryRow
                    key={rowKey(row)}
                    row={row}
                    onPatch={(patch) => patchRow(rowKey(row), patch)}
                    t={t}
                  />
                ))}
              </div>
            </section>
          );
        })}

        <section className="rounded-lg border border-dashed border-border bg-card px-5 py-5">
          <h2 className="text-sm font-semibold text-foreground">{t('Add a leave type')}</h2>
          <p className="mt-1 max-w-[60ch] text-sm text-muted-foreground">
            {t('Creates the leave type for this company only. Per-country rules become editable straight after.')}
          </p>
          <form className="mt-4 flex flex-wrap items-end gap-3" onSubmit={addLeaveType}>
            <div className="min-w-0 flex-1 sm:max-w-xs">
              <Input
                label={t('Leave type name')}
                data-testid="leave-config-add-type-name"
                value={newTypeName}
                maxLength={100}
                placeholder={t('Annual Leave')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewTypeName(e.target.value)}
              />
            </div>
            <Button
              type="submit"
              variant="outline"
              iconName="Plus"
              data-testid="leave-config-add-type-submit"
              loading={creating}
              disabled={creating || newTypeName.trim().length === 0}
            >
              {t('Add leave type')}
            </Button>
          </form>
        </section>
      </div>
    );
  };

  /**
   * The bar is a response to an edit, not page furniture. Every other StickyActionBar caller gates
   * the whole element at the call site (`{cond && <StickyActionBar …/>}` in ModuleConfig,
   * HRAdminPublicProfileSettings, LeaveBalances); none of them changes the component's API to do it.
   * This page follows that convention and adds the dirty state to its own condition, so a clean page
   * no longer ends on ~70px of bordered nothing holding two greyed-out buttons.
   *
   * `saving` keeps the bar mounted across the save round-trip: the dirty flag clears on success, and
   * unmounting the button mid-request would take the spinner with it.
   */
  const showActionBar =
    !!selectedCompanyId && !loadingConfig && !configError && groups.length > 0
    && (hasUnsavedChanges || saving);

  return (
    <PageLayout
      title={t('Leave Configuration')}
      description={t('Company-level leave types and their per-country rules')}
      icon={Palmtree}
      accentColor="emerald"
      maxWidth="6xl"
    >
      <div className="flex flex-col gap-6">
        {/*
          Both pickers are locked by the same condition, so the reason is stated once for the pair
          rather than repeated under each one.
        */}
        <div className="max-w-3xl">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              {loadingCompanies ? (
                <Skeleton className="h-10 w-full" />
              ) : companiesError ? (
                <Alert variant="error">
                  <span className="text-sm">{t('Could not load the company list.')}</span>
                </Alert>
              ) : (
                <Select
                  label={t('Select Company')}
                  data-testid="leave-config-company-picker"
                  options={companyOptions}
                  value={selectedCompanyId}
                  onChange={handleCompanyChange}
                  disabled={hasUnsavedChanges || saving}
                  searchable
                  placeholder={t('Choose a company...')}
                />
              )}
            </div>

            {!!selectedCompanyId && (
              <div className="flex flex-col gap-2">
                <Select
                  label={t('Countries')}
                  data-testid="leave-config-country-picker"
                  options={countryOptions}
                  value={effectiveCountryIds}
                  onChange={handleCountryChange}
                  multiple
                  searchable
                  clearable
                  loading={loadingCountries}
                  disabled={hasUnsavedChanges || saving}
                  placeholder={t('Company default')}
                />
                <Checkbox
                  label={t('Show every country')}
                  description={t('Lets you configure a country before anyone works there.')}
                  data-testid="leave-config-include-all-countries"
                  checked={includeAllCountries}
                  disabled={hasUnsavedChanges || saving}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIncludeAllCountries(e.target.checked)}
                />
              </div>
            )}
          </div>

          {hasUnsavedChanges && (
            <p className="mt-3 text-center text-sm text-muted-foreground" data-testid="leave-config-pickers-locked">
              {t('Save or reset your changes first.')}
            </p>
          )}
        </div>

        {saveError && (
          <Alert variant="error">
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium">{t('The leave configuration was not saved')}</span>
              <span className="text-sm">{saveError}</span>
              <span className="text-xs opacity-80">{t('Nothing was written — your edits are still on this page.')}</span>
            </div>
          </Alert>
        )}

        {renderBody()}

        {showActionBar && (
          <StickyActionBar
            hasUnsavedChanges={hasUnsavedChanges}
            isSaving={saving}
            onReset={() => selectedCompanyId && fetchConfig(selectedCompanyId, selectedCountryIds)}
            onSave={save}
            saveTestId="leave-config-save"
            resetTestId="leave-config-reset"
            t={t}
          />
        )}

        <ToastRenderer toast={toast} onDismiss={dismissToast} />
      </div>
    </PageLayout>
  );
}

/**
 * Legacy alias for the pre-sweep "Leaves Management" links
 * (`/settings/companies/:companyId/leavesManagement`, `/settings/attendance/leavesManagement`).
 *
 * Role-aware on purpose: Operator lands on the config-only page above (previously a hard 403, because
 * the old target `/leave-policies` is HR-only); every other role keeps the existing behaviour and goes
 * to /leave-policies. No role gains a surface it did not already have.
 */
export function LegacyLeavesManagementRedirect() {
  const { companyId } = useParams<{ companyId?: string }>();
  const role = useSelector((state: { user: { role: string } }) => state.user.role);

  if (role !== 'Operator') return <Navigate to="/leave-policies" replace />;

  const query = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
  return <Navigate to={`/settings/operator/leave-configuration${query}`} replace />;
}
